import { useEffect, useMemo, useState } from "react";
import api, { apiError, formatEuro, imageUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ImageUpload } from "@/components/ImageUpload";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, PencilSimple, Trash, X, BowlFood } from "@phosphor-icons/react";

const emptyForm = { nome: "", descrizione: "", prezzo_base: "", attivo: true, sezioni: [], foto: null };
const newSection = () => ({ nome: "", obbligatoria: true, max_scelte: "1", opzioni: [] });

export default function Bowls() {
  const [bowls, setBowls] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const { user } = useAuth();
  const isAdmin = ["admin", "master"].includes(user?.role);

  const load = () => {
    api.get("/bowls").then((r) => setBowls(r.data)).catch(() => {});
    api.get("/ingredients").then((r) => setIngredients(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const ingMap = useMemo(() => Object.fromEntries(ingredients.map((i) => [i.id, i])), [ingredients]);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (b) => {
    setEditing(b);
    setForm({
      nome: b.nome, descrizione: b.descrizione, prezzo_base: String(b.prezzo_base), attivo: b.attivo, foto: b.foto || null,
      sezioni: b.sezioni.map((s) => ({
        nome: s.nome, obbligatoria: s.obbligatoria, max_scelte: String(s.max_scelte),
        opzioni: s.opzioni.map((o) => ({ ingredient_id: o.ingredient_id, quantita: String(o.quantita), sovrapprezzo: String(o.sovrapprezzo) })),
      })),
    });
    setOpen(true);
  };

  const setSection = (si, patch) => setForm((f) => ({ ...f, sezioni: f.sezioni.map((s, i) => (i === si ? { ...s, ...patch } : s)) }));
  const setOption = (si, oi, patch) =>
    setSection(si, { opzioni: form.sezioni[si].opzioni.map((o, i) => (i === oi ? { ...o, ...patch } : o)) });

  const save = async () => {
    if (!form.nome) { toast.error("Il nome è obbligatorio"); return; }
    const payload = {
      nome: form.nome, descrizione: form.descrizione, prezzo_base: parseFloat(form.prezzo_base) || 0, attivo: form.attivo, foto: form.foto,
      sezioni: form.sezioni.filter((s) => s.nome).map((s) => ({
        nome: s.nome, obbligatoria: s.obbligatoria, max_scelte: parseInt(s.max_scelte) || 1,
        opzioni: s.opzioni.filter((o) => o.ingredient_id).map((o) => ({
          ingredient_id: o.ingredient_id, quantita: parseFloat(o.quantita) || 0, sovrapprezzo: parseFloat(o.sovrapprezzo) || 0,
        })),
      })),
    };
    try {
      if (editing) { await api.put(`/bowls/${editing.id}`, payload); toast.success("Bowl aggiornata"); }
      else { await api.post("/bowls", payload); toast.success("Bowl creata"); }
      setOpen(false);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Eliminare "${b.nome}"?`)) return;
    try { await api.delete(`/bowls/${b.id}`); toast.success("Bowl eliminata"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="bowls-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold">Bowl componibili</h1>
          <p className="text-[#6B7280] mt-1">Sezioni configurabili con sovrapprezzi per opzione.</p>
        </div>
        {isAdmin && (
          <Button onClick={openNew} data-testid="bowl-add-button" className="rounded-full bg-[#D95D39] hover:bg-[#BF4F2F] gap-2">
            <Plus size={18} weight="bold" /> Nuova bowl
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {bowls.map((b) => (
          <div key={b.id} className="bg-white border border-[#E5E5E0] rounded-2xl p-6 space-y-4" data-testid={`bowl-card-${b.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                {b.foto ? (
                  <img src={imageUrl(b.foto)} alt={b.nome} className="h-11 w-11 rounded-xl object-cover border border-[#E5E5E0]" />
                ) : (
                  <div className="h-11 w-11 rounded-xl bg-[#FAF9F6] border border-[#E5E5E0] flex items-center justify-center">
                    <BowlFood size={24} weight="duotone" className="text-[#D95D39]" />
                  </div>
                )}
                <div>
                  <p className="font-bold text-lg">{b.nome}</p>
                  <p className="text-sm text-[#6B7280]">Prezzo base: <span className="font-semibold text-[#1C1C1C]">{formatEuro(b.prezzo_base)}</span></p>
                </div>
              </div>
              {isAdmin && (
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(b)} data-testid={`bowl-edit-${b.id}`} className="hover:bg-stone-100"><PencilSimple size={18} /></Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(b)} data-testid={`bowl-delete-${b.id}`} className="hover:bg-red-50 text-[#D32F2F]"><Trash size={18} /></Button>
                </div>
              )}
            </div>
            <div className="space-y-3">
              {b.sezioni.map((s, i) => (
                <div key={i} className="border border-[#E5E5E0] rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2.5">
                    <p className="font-semibold text-sm">{s.nome}</p>
                    <Badge variant="outline" className="rounded-full text-[10px]">{s.obbligatoria ? "Obbligatoria" : "Facoltativa"} · max {s.max_scelte}</Badge>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {s.opzioni.map((o, j) => (
                      <span key={j} className="text-xs bg-[#FAF9F6] border border-[#E5E5E0] rounded-full px-3 py-1.5">
                        {o.nome} · {o.quantita}{ingMap[o.ingredient_id]?.unita || "g"} · {o.nutrizione.kcal} kcal
                        {o.sovrapprezzo > 0 && <span className="text-[#D95D39] font-semibold"> +{formatEuro(o.sovrapprezzo)}</span>}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="bowl-dialog">
          <DialogHeader><DialogTitle>{editing ? "Modifica bowl" : "Nuova bowl"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Nome</Label>
                <Input value={form.nome} onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))} data-testid="bowl-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Prezzo base (€)</Label>
                <Input type="number" step="0.10" value={form.prezzo_base} onChange={(e) => setForm((f) => ({ ...f, prezzo_base: e.target.value }))} data-testid="bowl-price-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Descrizione</Label>
              <Input value={form.descrizione} onChange={(e) => setForm((f) => ({ ...f, descrizione: e.target.value }))} data-testid="bowl-description-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Foto</Label>
              <ImageUpload value={form.foto} onChange={(p) => setForm((f) => ({ ...f, foto: p }))} testId="bowl-photo" />
            </div>

            <div className="flex items-center justify-between">
              <p className="font-semibold">Sezioni</p>
              <Button variant="outline" size="sm" onClick={() => setForm((f) => ({ ...f, sezioni: [...f.sezioni, newSection()] }))} data-testid="bowl-add-section-button" className="rounded-full gap-1.5 hover:bg-stone-50">
                <Plus size={14} /> Aggiungi sezione
              </Button>
            </div>

            {form.sezioni.map((s, si) => (
              <div key={si} className="border border-[#E5E5E0] rounded-xl p-4 space-y-3 bg-[#FAF9F6]" data-testid={`bowl-section-${si}`}>
                <div className="flex items-end gap-3">
                  <div className="flex-1 space-y-1.5">
                    <Label className="text-xs">Nome sezione</Label>
                    <Input className="bg-white" placeholder="Es. Base, Proteina, Topping" value={s.nome} onChange={(e) => setSection(si, { nome: e.target.value })} data-testid={`bowl-section-name-${si}`} />
                  </div>
                  <div className="w-24 space-y-1.5">
                    <Label className="text-xs">Max scelte</Label>
                    <Input className="bg-white" type="number" min="1" value={s.max_scelte} onChange={(e) => setSection(si, { max_scelte: e.target.value })} data-testid={`bowl-section-max-${si}`} />
                  </div>
                  <div className="flex items-center gap-2 pb-2.5">
                    <Checkbox checked={s.obbligatoria} onCheckedChange={(v) => setSection(si, { obbligatoria: !!v })} data-testid={`bowl-section-required-${si}`} />
                    <Label className="text-xs">Obbligatoria</Label>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => setForm((f) => ({ ...f, sezioni: f.sezioni.filter((_, i) => i !== si) }))} data-testid={`bowl-section-remove-${si}`} className="text-[#D32F2F] hover:bg-red-50"><Trash size={16} /></Button>
                </div>
                {s.opzioni.map((o, oi) => (
                  <div key={oi} className="flex items-center gap-2">
                    <Select value={o.ingredient_id} onValueChange={(v) => setOption(si, oi, { ingredient_id: v })}>
                      <SelectTrigger className="flex-1 bg-white" data-testid={`bowl-option-ingredient-${si}-${oi}`}><SelectValue placeholder="Ingrediente" /></SelectTrigger>
                      <SelectContent>{ingredients.map((i) => <SelectItem key={i.id} value={i.id}>{i.nome} ({i.categoria})</SelectItem>)}</SelectContent>
                    </Select>
                    <Input type="number" className="w-20 bg-white" title="Quantità" value={o.quantita} onChange={(e) => setOption(si, oi, { quantita: e.target.value })} data-testid={`bowl-option-qty-${si}-${oi}`} />
                    <span className="text-xs text-[#6B7280] w-5">{ingMap[o.ingredient_id]?.unita || "g"}</span>
                    <Input type="number" step="0.10" className="w-24 bg-white" title="Sovrapprezzo €" placeholder="+ €" value={o.sovrapprezzo} onChange={(e) => setOption(si, oi, { sovrapprezzo: e.target.value })} data-testid={`bowl-option-extra-${si}-${oi}`} />
                    <Button variant="ghost" size="icon" onClick={() => setSection(si, { opzioni: s.opzioni.filter((_, i) => i !== oi) })} data-testid={`bowl-option-remove-${si}-${oi}`} className="text-[#D32F2F] hover:bg-red-50"><X size={14} /></Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => setSection(si, { opzioni: [...s.opzioni, { ingredient_id: "", quantita: "100", sovrapprezzo: "0" }] })} data-testid={`bowl-add-option-${si}`} className="rounded-full gap-1.5 hover:bg-white">
                  <Plus size={13} /> Aggiungi opzione
                </Button>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Annulla</Button>
            <Button onClick={save} data-testid="bowl-save-button" className="rounded-full bg-[#D95D39] hover:bg-[#BF4F2F]">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
